import { Injectable, NgZone, Injector } from "@angular/core";
import { Router } from "@angular/router";
import { interval, Observable, Subscription, delay } from "rxjs";
import { ForegroundService } from "@capawesome-team/capacitor-android-foreground-service";
import { FirmwareService } from "@shared/state/firmware/firmware.service";

import {
  BluetoothLE,
  BondedStatus,
  CharacteristicParams,
  Characteristics,
  CurrConnectionStatus,
  DescriptorParams,
  Descriptors,
  Device,
  DeviceInfo,
  MTU,
  OperationResult,
  ScanParams,
  ScanStatus,
  Service,
  Services,
} from "@ionic-native/bluetooth-le/ngx";
import { BLE, BLEScanOptions } from "@awesome-cordova-plugins/ble/ngx";
import { AlertController, Platform } from "@ionic/angular";

import { DeviceQuery } from "@state/device/device.query";

import { ICurrentDevice, IPreviousDevice } from "@interfaces/device.interface";
import { WorkerMessage } from "@shared/worker/models/worker-message.model";

import { takeWhile } from "rxjs/operators";
import { AlarmQuery } from "@shared/state/alarm/alarm.query";

import { IAlarm } from "@shared/interfaces/alarm.interface";

// import { BluetoothCore } from '@manekinekko/angular-web-bluetooth';
import { AnyRecord } from "dns";
import { FirmwareQuery } from "@shared/state/firmware/firmware.query";

import { WORKER_TOPIC } from "@shared/worker/models/worker-topic.constants";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { UserQuery } from "@shared/state/user/user.query";

import { Capacitor } from "@capacitor/core";

/********************services********************************* */
import { DeviceService } from "@state/device/device.service";
import { RemoteUserService } from "@shared/state/remote-user/remote-user.service";
import { ToastService } from "../toast.service";
import { AlarmService } from "@shared/state/alarm/alarm.service";
import { TranslateService } from "@ngx-translate/core";
import { WorkerService } from "@shared/worker/worker.service";
import { BleWebService } from "./ble-web.service";
import { UserService } from "@state/user/user.service";

/**********************encryption************************* */

import { BleSecureService } from "@shared/services/ble-secure.service";

@Injectable({
  providedIn: "root",
})
export class BleCapacitorService {
  private triedToReconnect = false;
  private runTick = true;
  private sendQueue: number[][] = [];
  private timeStampDebug: number;
  private alarmSubscription: Subscription;
  private alarmClearSubscription: Subscription;
  private connectSubscription: Subscription;
  private alarmLogSubscription: Subscription;
  private alarmLogGetSubscription: Subscription;

  private bleQueue: Uint8Array[] = [];
  private sending = false;

  // batching knobs (tune these)
  private batchEnabled = true;
  private maxBatchBytes = 224; // total plaintext bytes per batch
  private maxBatchFrames = 10; // max frames per batch
  private maxBatchDelayMs = 5; // optional: wait a few ms to gather more frames

  hx = (u8: Uint8Array, n = 16) =>
    Array.from(u8.slice(0, n))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join(" ");

  private _toastService: ToastService | null = null;

  private isScanningBol: boolean = false;
  private discoveredDevices = new Set<string>();

  constructor(
    private ble: BluetoothLE,
    private deviceService: DeviceService,
    private router: Router,
    private ngZone: NgZone,
    private deviceQuery: DeviceQuery,
    private platform: Platform,

    private injector: Injector,
    private alarmService: AlarmService,
    private alarmQuery: AlarmQuery,
    private translateService: TranslateService,
    private alertController: AlertController,
    private bleWebService: BleWebService,
    private remoteUserService: RemoteUserService,
    private userService: UserService,
    private newBle: BLE,
    private firmwareQuery: FirmwareQuery,
    private workerService: WorkerService,
    private userQuery: UserQuery,
    private secureSvc: BleSecureService,
    private firmwareService: FirmwareService,
  ) {
    this.timeStampDebug = 0;
  }

  private get toastService(): ToastService {
    if (!this._toastService) {
      this._toastService = this.injector.get(ToastService);
    }
    return this._toastService;
  }

  public async isEnabled(): Promise<{ isEnabled: boolean }> {
    return await this.ble.isEnabled();
  }

  public async hasPermission(): Promise<{ hasPermission: boolean }> {
    return await this.ble.hasPermission();
  }

  public async requestPermission(): Promise<{ requestPermission: boolean }> {
    return await this.ble.requestPermission();
  }

  public async isLocationEnabled(): Promise<{ isLocationEnabled: boolean }> {
    return await this.ble.isLocationEnabled();
  }

  public async requestLocation(): Promise<{ requestLocation: boolean }> {
    return await this.ble.requestLocation();
  }

  private prepareDisconnect(bleChip: string) {
    this.secureSvc.disable();
    this.userService.updateLatestConnectionInfo();

    this.alarmSubscription?.unsubscribe();
    this.alarmClearSubscription?.unsubscribe();
    this.alarmLogSubscription?.unsubscribe();
    this.alarmLogGetSubscription?.unsubscribe();
    this.connectSubscription?.unsubscribe();
    this.unsubscribe(
      bleChip,
      this.deviceQuery.characteristicsUuid,
      this.deviceQuery.serviceUuid,
    );

    this.remoteUserService.resetStoreTotally();

    this.deviceService.resetDeviceData();
    this.alarmService.resetAlarmData();

    const clearWorkerState = new WorkerMessage(
      WORKER_TOPIC.clearWorkerState,
      "",
    );

    this.workerService.doWork(clearWorkerState);

    this.deviceService.stopConnection();
    this.nativeDisconnectToDevice();

    this.stopForegroundService();
    this.close();
  }

  public async disconnect(currentDevice: ICurrentDevice): Promise<any> {
    this.prepareDisconnect(currentDevice.currentConnectedBluetoothChip);
    currentDevice.hasBLEConnection = false;

    return await this.newBle.disconnect(
      currentDevice.currentConnectedBluetoothChip,
    );
  }
  public handleDisconnectForSparePart(): void {
    this.disconnect(this.deviceQuery.currentDevice);
    this.remoteUserService.setRemoteSessionInActive;
  }

  public read(address: string, characteristic: string, service: string) {
    /* console.log(
      "address, service, characteristic",
      address,
      service,
      characteristic,
    );*/
    return this.newBle.read(address, service, characteristic);
  }

  public subscribe(address: string, characteristic: string, service: string) {
    const params: DescriptorParams = {
      address,
      service,
      characteristic,
    };
    return this.newBle.startNotification(address, service, characteristic);
  }

  public unsubscribe(address: string, characteristic: string, service: string) {
    const params: DescriptorParams = {
      address,
      characteristic,
      service,
    };
    return this.newBle.stopNotification(address, service, characteristic);
  }

  public initialize() {
    // console.log("initializing ble");
    return this.newBle.isEnabled();
  }

  public initializeIOS() {
    return this.ble.initialize();
  }

  public startScan(): Observable<any> {
    return new Observable((observer) => {
      if (this.isScanningBol) {
        observer.error("Scan already in progress");
        return;
      }

      this.isScanningBol = true;
      this.deviceService.updateIsScanning(true);

      const discoveredDevices = new Set<string>();

      const performScans = async () => {
        for (let i = 0; i < 3; i++) {
          await new Promise<void>((resolve) => {
            const sub = this.newBle.scan([], 7).subscribe({
              next: (device) => {
                const deviceId = device.id || device.address;
                if (!discoveredDevices.has(deviceId)) {
                  discoveredDevices.add(deviceId);
                  observer.next(device);
                }
              },
              error: () => resolve(),
              complete: () => resolve(),
            });
          });

          // Wait 1 second before next scan (except after last scan)
          if (i < 2) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        }

        observer.complete();
        this.cleanupScan();
      };

      performScans().catch((err) => {
        observer.error(err);
        this.cleanupScan();
      });

      return () => {
        this.newBle.stopScan().catch(() => {});
        this.cleanupScan();
      };
    });
  }

  private cleanupScan() {
    this.isScanningBol = false;
    this.deviceService.updateIsScanning(false);
  }

  public async isScanning(): Promise<{ isScanning: boolean }> {
    return await this.ble.isScanning();
  }

  public async stopScan(): Promise<any> {
    this.deviceService.updateIsScanningForPreviousDevices(false);
    this.deviceService.updateIsScanning(false);
    return this.newBle.stopScan().then(() => console.log("Stopped scan"));
  }

  public async retrieveConnected(services: string[]): Promise<DeviceInfo[]> {
    const params = {
      services,
    };
    const data = await this.ble.retrieveConnected(params);
    return JSON.parse(JSON.stringify(data)) as DeviceInfo[];
  }

  public scan(services: string[]): Observable<any> {
    const params = {
      services,
    };
    return this.newBle.scan(services, 15);
  }

  public connect(address: string): Observable<any> {
    const params = {
      address,
      autoConnect: true,
    };

    return this.newBle.connect(address);
  }

  public async mtu(address: string): Promise<MTU> {
    return await this.newBle.requestMtu(address, 512);
  }

  public async close() {
    //this.firmwareService.setOngoing(false);
    this.deviceService.removeCurrentDevice();
  }

  public async isConnected(address: string): Promise<any> {
    const params = { address };
    // console.log("params", params);
    return await this.newBle.isConnected(address);
  }

  public async discover(address: string): Promise<Device> {
    const params = { address, clearCache: true };
    return await this.ble.discover(params);
  }

  public runMtu = true;

  private async writeWeb(bytes: Uint8Array): Promise<void> {
    if (this.bleQueue.length === 0) {
      this.bleQueue.push(bytes);
      this.sendAll();
    } else if (this.bleQueue.length === 1) {
      this.bleQueue.push(bytes);
    } else {
      if (
        this.bleQueue[this.bleQueue.length - 1].length + bytes.length <=
        244
      ) {
        this.bleQueue[this.bleQueue.length - 1] = new Uint8Array([
          ...this.bleQueue[this.bleQueue.length - 1],
          ...bytes,
        ]);
      } else {
        this.bleQueue.push(bytes);
      }
    }
  }

  /***********************encryption****************************** */

  private concatU8(chunks: Uint8Array[]): Uint8Array {
    if (chunks.length === 1) return chunks[0];
    const total = chunks.reduce((n, c) => n + c.length, 0);
    const out = new Uint8Array(total);
    let off = 0;
    for (const c of chunks) {
      out.set(c, off);
      off += c.length;
    }
    return out;
  }

  public async write(plaintext: Uint8Array) {
    if (!(plaintext instanceof Uint8Array))
      throw new Error("write expects Uint8Array");

    this.bleQueue.push(plaintext.slice());
    if (!this.sending) void this.sendAll();
  }

  private async sendAll() {
    if (this.sending) return;
    this.sending = true;

    try {
      while (this.bleQueue.length > 0) {
        const batch: Uint8Array[] = [];
        let total = 0;

        // Always take the first message
        const first = this.bleQueue.shift()!;
        batch.push(first);
        total += first.length;

        // ✅ Only batch if there are MORE messages waiting right now
        if (this.bleQueue.length > 0) {
          while (
            this.bleQueue.length > 0 &&
            batch.length < this.maxBatchFrames &&
            total + this.bleQueue[0].length <= this.maxBatchBytes
          ) {
            const next = this.bleQueue.shift()!;
            batch.push(next);
            total += next.length;
          }
        }

        const batchPt = this.concatU8(batch); // plaintext blob for this batch

        // --- Slice to transport and send sequentially (NO coalescing) ---
        const isWeb =
          this.platform.is("mobileweb") || this.platform.is("desktop");

        const maxChunk = isWeb ? /* e.g. */ 224 : 224;

        for (let i = 0; i < batchPt.length; i += maxChunk) {
          const slice = batchPt.slice(i, i + maxChunk);
          const payload = this.secureSvc?.secure
            ? await this.secureSvc.encryptRawBeforeWrite(slice) // RAW CBC ciphertext (16B-multiple)
            : slice; // plaintext legacy mode

          if (isWeb) {
            const ch = this.bleWebService?.characteristics?.[0];
            if (!ch) throw new Error("Web characteristic missing");

            await ch.writeValueWithoutResponse(new Uint8Array(payload));
          } else {
            const address =
              this.deviceQuery.currentDevice?.currentConnectedBluetoothChip;
            const service = this.deviceQuery.serviceUuid;
            const characteristic = this.deviceQuery.characteristicsUuid;
            if (!address) throw new Error("Native address missing");

            this.newBle.writeWithoutResponse(
              address,
              service,
              characteristic,
              new Uint8Array(payload).buffer,
            );
          }
        }
      }
    } catch (e) {
      console.error("[BLE] sendAll error:", e);
    } finally {
      this.sending = false;
      if (this.bleQueue.length > 0) void this.sendAll();
    }
  }

  public async addBleDataToOutQueue(bytes: number[]) {
    if (this.platform.is("mobileweb") || this.platform.is("desktop")) {
      // this.sendQueue.push(bytes);
      this.write(new Uint8Array(bytes));
    } else {
      this.write(new Uint8Array(bytes));
    }

    // console.log("data written to queue", bytes);
  }
  private RunSendThread() {
    interval(1)
      .pipe(takeWhile(() => this.runTick))
      .subscribe(() => {
        let joinedData: number[][] = [];
        let timeMs = new Date().valueOf();
        // console.log("mata");

        while (this.sendQueue.length > 0) {
          /*Pull all available data from out queue*/
          let data = this.sendQueue[0];
          joinedData.push(data);
          this.sendQueue.shift();
        }
        if (joinedData.length > 0) {
          /*Concat outgoing data to one array and call write*/
          const joinedDataAsOneArray = joinedData.reduce(
            (acc: number[], curr) => acc.concat(curr),
            [],
          );
          this.write(new Uint8Array(joinedDataAsOneArray));
        }
      });
  }
  /**
   * iOS specific services
   */
  public async services(
    address: string,
    services: string[],
  ): Promise<Services> {
    const params = { address, services };
    return await this.ble.services(params);
  }

  public async characteristics(
    address: string,
    service: string,
    characteristics: string[],
  ): Promise<{ characteristics: Characteristics }> {
    const params: CharacteristicParams = { address, service, characteristics };
    return await this.ble.characteristics(params);
  }

  public async descriptors(
    address: string,
    service: string,
    characteristic: string,
  ): Promise<{ descriptors: Descriptors }> {
    const params: DescriptorParams = { address, service, characteristic };
    return await this.ble.descriptors(params);
  }

  /**
   * Android specific services
   */
  public enable(): Promise<any> {
    //  console.log("enabled");
    return this.newBle.enable();
  }

  public showSettings() {
    this.newBle.showBluetoothSettings();
  }

  public disable(): void {
    this.ble.disable();
  }

  public bond(address: string): Observable<{ status: DeviceInfo }> {
    const params = { address };
    return this.ble.bond(params); // .pipe(take(2));
  }

  public async unbond(address: string): Promise<{ status: DeviceInfo }> {
    const params = { address };
    return await this.ble.unbond(params);
  }

  public async requestConnectionPriority(
    address: string,
    connectionPriority: "low" | "balanced" | "high",
  ): Promise<DeviceInfo> {
    const params = { address, connectionPriority };
    return await this.ble.requestConnectionPriority(params);
  }

  public async isBonded(address: string): Promise<BondedStatus> {
    const params = { address };
    return await this.ble.isBonded(params);
  }

  /**
   * Functions
   */
  public encodedStringToBytes(val: string): Uint8Array {
    return this.ble.encodedStringToBytes(val);
  }

  public bytesToEncodedString(val: Uint8Array): string {
    return this.ble.bytesToEncodedString(val);
  }

  public stringToBytes(val: string): Uint8Array {
    return this.ble.stringToBytes(val);
  }

  public bytesToString(val: Uint8Array): string {
    return this.ble.bytesToString(val);
  }

  /**
   * Operator specific functions
   */

  public handleCharacteristicValueChanged(event: Event) {
    console.log(event);
    // const value = event.target.value;
    // console.log('Received ' + value);
    // TODO: Parse Heart Rate Measurement value.
    // See https://github.com/WebBluetoothCG/demos/blob/gh-pages/heart-rate-sensor/heartRateSensor.js
  }

  public async connectToDevice(address: string, name: string): Promise<void> {
    //console.log("connectToDevice");
    this.triedToReconnect = false;
    this.deviceService.resetDeviceData();
    this.deviceService.addInitialName(name);
    this.alarmService.resetAlarmData();
    this.subscribeToAlarms();
    this.isConnected(address)
      .then(async (isConnected) => {
        if (isConnected.isConnected) {
          setTimeout(() => {
            this.startSubscribeConnection(address, name);
          }, 2000);
        } else {
          this.startSubscribeConnection(address, name);
        }
      })
      .catch((err) => {
        console.error("err: ", err);
        this.startSubscribeConnection(address, name);
      });
  }

  public subscribeToAlarms() {
    this.alarmSubscription?.unsubscribe();
    this.alarmSubscription = this.alarmQuery.alarms$.subscribe((alarms) => {
      if (alarms.length > 0) {
        alarms.forEach((alarm) => {
          if (alarm && alarm.isOldAlarm === false) {
            this.alarmService.setAlarmToOld(alarm);
          }
        });
      }
    });

    // For alarm clear from servicetool
    this.alarmClearSubscription = this.alarmQuery.pendingAlarmClear$.subscribe(
      (alarms) => {
        if (!alarms?.length) {
          return;
        }

        alarms.forEach((alarm) => {
          this.alarmService.removeAlarm(alarm, alarm.requestUid);
          this.alarmService.removeAlarmToBeCleared(alarm);
        });
      },
    );

    // For alarmlog clear from servicetool
    this.alarmLogSubscription = this.alarmQuery.pendingAlarmLogClear$.subscribe(
      (req) => {
        if (req && req.length > 0) {
          req.forEach((val) => {
            this.alarmService.clearLog(val.clearType, val.requestUid);
            this.alarmService.removeClearAlarmLog();
          });
        }
      },
    );

    this.alarmLogGetSubscription = this.alarmQuery.pendingGetLog$.subscribe(
      (req) => {
        if (req && req > 0) {
          const setupMessage = new WorkerMessage(WORKER_TOPIC.getAlarmLog, {});
          this.workerService.doWork(setupMessage);
          this.alarmService.removeGetAlarmLog();
        }
      },
    );
  }

  // IS NOT IN USE ANYMORE AFTER NEW DESIGN 2022 OCTOBER
  public async showNewAlarm(alarm: IAlarm): Promise<void> {
    // console.log(alarm);

    const dismiss = this.translateService.instant("alerts.dismiss");
    const later = this.translateService.instant("alerts.later");
    const alert = await this.alertController.create({
      header: "New Alarm",
      subHeader:
        "Name: " +
        alarm.Name +
        ", ECU ID: " +
        alarm.ecuId +
        ", Error Code ID: " +
        alarm.errorCodeId,
      message: alarm.comment,
      cssClass: "alert-wrapper",
      keyboardClose: false,
      backdropDismiss: false,
      buttons: alarm.alarmInfoList.AckAlarm
        ? [
            {
              text: dismiss,
              role: "dismiss",
            },
            {
              text: later,
              role: "later",
            },
          ]
        : [
            {
              text: "OK",
              role: "ok",
            },
          ],
    });
    alert.present();

    alert.onDidDismiss().then(async (res) => {
      //console.log('alert res: ', res);
      switch (res.role) {
        case "dismiss":
          this.alarmService.removeAlarm(alarm);
          break;
        case "later":
          this.toastService.dismisstoast();
          this.toastService.infoToast("toasts.alarm dismiss later", true);
          break;
        case "ok":
          this.toastService.dismisstoast();
        default:
          break;
      }
    });
  }

  public startSubscribeConnection(address: string, name: string): void {
    //console.log("startSubscribeConnection");
    this.deviceService.addInitialName(name);
    const isAndroid = Capacitor.getPlatform() === "android";
    console.log("isAndroid", Capacitor.getPlatform() === "android");
    this.initialize()
      .then(async (init) => {
        console.log("init", init);
      })
      .then(() => {
        this.connectSubscription = this.connect(address).subscribe({
          next: async (deviceInfo) => {
            if (deviceInfo) {
              console.log("deviceInfo", deviceInfo, address);

              if (isAndroid) {
                await ForegroundService.startForegroundService({
                  id: 1001,
                  title: "Connected",
                  body: "Keeping BLE alive",
                  smallIcon: "",
                });

                const mtu = await this.mtu(address);
                   console.log("mtu", mtu);
                await this.nativeConnectToDevice(address);
                if (mtu) {
                  const newDevice: ICurrentDevice = {
                    name: deviceInfo.name,
                    status: "waiting for serialnumber",
                    services: deviceInfo.characteristics,
                    bluetoothConnectors: [address],
                    currentConnectedBluetoothChip: address,
                    hasBLEConnection: true,
                  };
                  this.ngZone.run(() => {
                    this.triedToReconnect = false;
                    this.deviceService.addCurrentDevice(newDevice);
                    this.deviceService.stopConnection();
                    this.router.navigate(["/"]);
                  });
                }
              } else {
                const newDevice: ICurrentDevice = {
                  name: deviceInfo.name,
                  status: "waiting for serialnumber",
                  services: deviceInfo.characteristics,
                  bluetoothConnectors: [address],
                  currentConnectedBluetoothChip: address,
                  hasBLEConnection: true,
                };
                this.ngZone.run(() => {
                  this.triedToReconnect = false;
                  this.deviceService.addCurrentDevice(newDevice);
                  this.deviceService.stopConnection();
                  this.router.navigate(["/"]);
                });
              }
            } else if (deviceInfo.status === "disconnected") {
              this.deviceService.updateLastConnected();

              this.deviceService.resetDeviceData();
              this.alarmService.resetAlarmData();
              this.router.navigate(["/"]);
              await this.stopForegroundService();
              await this.nativeDisconnectToDevice();

              if (!this.triedToReconnect) {
                this.deviceService.startReconnectTimer(7);
                var reConnectInterval = setInterval(() => {
                  this.deviceService.updateReconnectTimer();
                }, 1000);
                setTimeout(() => {
                  this.ble.close({ address }).then((res) => {
                    // console.log('close: ', close, 'res: ', res);
                    this.startSubscribeConnection(address, name);
                    this.triedToReconnect = true;
                    clearInterval(reConnectInterval);
                    this.deviceService.startReconnectTimer(0);
                  });
                }, 7000);
              }
            }
          },
          error: async (err) => {
            console.error(err, err.errorMessage);
            if (err.errorMessage === "Peripheral Disconnected") {
              /*
                if (this.firmwareQuery.ongoingUpgrade && err.name.includes('Control')){
                  console.log ('this.firmwareQuery.ongoingUpgrade', this.firmwareQuery.ongoingUpgrade)
                  this.firmwareService.setOngoing(false);
                   console.log ('this.firmwareQuery.ongoingUpgrade', this.firmwareQuery.ongoingUpgrade)

                }*/
              // console.log ('this.firmwareQuery.ongoingUpgrade', this.firmwareQuery.ongoingUpgrade)
              this.deviceService.updateLastConnected();
              this.deviceService.resetDeviceData();
              this.alarmService.resetAlarmData();
              this.router.navigate(["/"]);

              if (!this.triedToReconnect) {
                this.deviceService.startReconnectTimer(7);
                var reConnectInterval = setInterval(() => {
                  this.deviceService.updateReconnectTimer();
                }, 1000);

                setTimeout(() => {
                  this.secureSvc.disable();
                  this.deviceService.startConnection();
                  this.startSubscribeConnection(address, name);
                  this.triedToReconnect = true;
                  clearInterval(reConnectInterval);
                  this.deviceService.startReconnectTimer(0);
                }, 7000);
              }
              // this.firmwareService.setOngoing(false);
              this.deviceService.stopConnection();
              await this.stopForegroundService();
              await this.nativeDisconnectToDevice();
              this.close();
            } else if (err.errorMessage === "Bluetooth Disabled") {
              this.deviceService.updateLastConnected();
              this.toastService.warningToast("ble.disabled", true);
              this.deviceService.resetDeviceData();
              this.alarmService.resetAlarmData();
              this.deviceService.updateIsBluetoothIsActive(false);
              this.router.navigate(["/"]);
              await this.stopForegroundService();
              this.deviceService.stopConnection();
              await this.nativeDisconnectToDevice();
              this.close();
            } else {
              this.deviceService.updateLastConnected();
              this.toastService.warningToast("ble.general error", true);
              this.deviceService.resetDeviceData();
              this.alarmService.resetAlarmData();
              this.deviceService.updateIsBluetoothIsActive(false);
              this.router.navigate(["/"]);
              await this.stopForegroundService();
              this.deviceService.stopConnection();
              await this.nativeDisconnectToDevice();
              this.close();
            }
          },
        });
      })
      .catch((err) => {
        console.log(err);
      });
  }

  async startForegroundService() {
    const isAndroid = Capacitor.getPlatform() === "android";
    if (isAndroid) {
      if (
        window.NativeBridge &&
        window.NativeBridge?.startBLEForegroundService
      ) {
        window.NativeBridge.startBLEForegroundService();
      } else {
        console.error(
          "Native method startBLEForegroundService is not available.",
        );
      }
    }
  }

  async stopForegroundService() {
    await ForegroundService.stopForegroundService();
  }

  async nativeConnectToDevice(address: string) {
    const isAndroid = Capacitor.getPlatform() === "android";
    if (isAndroid) {
      if (window.NativeBridge) {
        window.NativeBridge.connectToDevice(address);
      } else {
        console.error("Native method connectToDevice is not available.");
      }
    }
  }
  async nativeDisconnectToDevice() {
    const isAndroid = Capacitor.getPlatform() === "android";
    if (isAndroid) {
      if (window.NativeBridge) {
        window.NativeBridge.disconnectFromDevice();
      } else {
        console.error("Native method disconnectFromDevice is not available.");
      }
    }
  }
}
